import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { LuCircleCheck, LuFolderOpen, LuInfo, LuSearch, LuSearchX, LuTriangleAlert, LuX } from 'react-icons/lu';
import Button from '../../components/ui/Button';
import GlassIcon from '../../components/ui/GlassIcon';
import Modal from '../../components/ui/Modal';
import { getInitials } from '../../components/ui/Avatar';
import { GLASS_CHIP, GLASS_FIELD } from '../../components/ui/glass';
import { useTranslation } from '../../i18n/LanguageContext';
import { REQUIRED_CV_DOCUMENT_TYPES } from '../../utils/cvDocuments';
import { cvTypeLabel } from './employeeForm/employeeFormOptions';

const EmptyState = ({ icon: Icon, tone = 'slate', children }) => (
  <div className="tw-flex tw-flex-col tw-items-center tw-gap-3 tw-px-6 tw-py-10 tw-text-center">
    <span
      className={`tw-grid tw-h-12 tw-w-12 tw-place-items-center tw-rounded-2xl ${
        tone === 'rose'
          ? 'tw-bg-rose-500/10 tw-text-rose-500'
          : tone === 'emerald'
            ? 'tw-bg-emerald-500/10 tw-text-emerald-500'
            : 'tw-bg-slate-100 tw-text-slate-400 dark:tw-bg-white/[0.05] dark:tw-text-slate-500'
      }`}
    >
      <Icon aria-hidden="true" className="tw-h-6 tw-w-6" />
    </span>
    <p className="tw-max-w-sm tw-text-sm tw-font-medium tw-text-slate-600 dark:tw-text-slate-300">{children}</p>
  </div>
);

EmptyState.propTypes = {
  icon: PropTypes.elementType.isRequired,
  tone: PropTypes.oneOf(['slate', 'rose', 'emerald']),
  children: PropTypes.node,
};

/**
 * Lists active employees with missing required CV/GCP/IATA documents.
 * `onOpenFiles(employee)` opens that employee's file modal on the page; pass null to hide it.
 */
const MissingDocumentsModal = ({ rows, loading, error, onClose, onOpenFiles }) => {
  const { t, language } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');

  const countsByType = useMemo(() =>
    REQUIRED_CV_DOCUMENT_TYPES.map(type => ({
      ...type,
      count: rows.filter(r => r.missing.some(m => m.key === type.key)).length
    })), [rows]);

  const term = searchTerm.trim().toLocaleLowerCase('tr');
  const visibleRows = term
    ? rows.filter(({ employee }) =>
        `${employee.eName || ''} ${employee.eSurname || ''}`.toLocaleLowerCase('tr').includes(term))
    : rows;

  const renderList = () => {
    if (loading) {
      return (
        <ul aria-label={t('employees.modals.files.loading')} className="tw-space-y-2">
          {[0, 1, 2, 3].map((item) => (
            <li key={item} className="tw-h-[4.25rem] tw-animate-pulse tw-rounded-2xl tw-bg-slate-100 dark:tw-bg-white/[0.05]" />
          ))}
        </ul>
      );
    }
    if (error) return <EmptyState icon={LuTriangleAlert} tone="rose">{t('employees.modals.missingDocs.loadError')}</EmptyState>;
    if (visibleRows.length === 0) {
      return term
        ? <EmptyState icon={LuSearchX}>{t('employees.modals.missingDocs.noMatch')}</EmptyState>
        : <EmptyState icon={LuCircleCheck} tone="emerald">{t('employees.modals.missingDocs.none')}</EmptyState>;
    }
    return (
      <ul className="tw-space-y-2">
        {visibleRows.map(({ employee, missing }) => (
          <li key={employee.id} className={`tw-flex tw-items-center tw-gap-3 tw-rounded-2xl tw-p-3 ${GLASS_CHIP}`}>
            <span
              aria-hidden="true"
              className="tw-grid tw-h-10 tw-w-10 tw-shrink-0 tw-place-items-center tw-rounded-full tw-bg-gradient-to-br tw-from-teal-400 tw-to-cyan-600 tw-text-xs tw-font-bold tw-text-white tw-shadow-[inset_0_1px_0_rgba(255,255,255,0.45)]"
            >
              {getInitials(employee.eName, employee.eSurname)}
            </span>
            <div className="tw-min-w-0 tw-flex-1">
              <p className="tw-truncate tw-text-sm tw-font-semibold tw-text-slate-900 dark:tw-text-white">
                {employee.eName} {employee.eSurname}
              </p>
              <div className="tw-mt-1.5 tw-flex tw-flex-wrap tw-gap-1.5">
                {missing.map(m => (
                  <span
                    key={m.key}
                    className="tw-inline-flex tw-items-center tw-rounded-full tw-bg-rose-500/10 tw-px-2 tw-py-0.5 tw-text-[11px] tw-font-semibold tw-text-rose-700 tw-ring-1 tw-ring-inset tw-ring-rose-500/20 dark:tw-text-rose-300"
                  >
                    {cvTypeLabel(t, m.key, m.label)}
                  </span>
                ))}
              </div>
            </div>
            {onOpenFiles && (
              <Button
                size="sm"
                onClick={() => onOpenFiles(employee)}
                title={t('employees.modals.missingDocs.openFilesHint')}
                aria-label={t('employees.modals.missingDocs.openFilesHint')}
              >
                <LuFolderOpen aria-hidden="true" className="tw-h-4 tw-w-4 tw-text-violet-600 dark:tw-text-violet-300" />
                <span className="tw-hidden sm:tw-inline">{t('employees.modals.missingDocs.openFiles')}</span>
              </Button>
            )}
          </li>
        ))}
      </ul>
    );
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      size="lg"
      title={t('employees.modals.missingDocs.title')}
      subtitle={loading ? undefined : t('employees.modals.missingDocs.subtitle', { count: rows.length })}
      icon={(
        <GlassIcon gradient="tw-from-amber-300 tw-to-orange-500 tw-shadow-amber-500/30" size="xs">
          <LuTriangleAlert aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
        </GlassIcon>
      )}
      closeLabel={t('common.close')}
      lang={language}
      footer={<Button variant="ghost" onClick={() => onClose()}>{t('common.close')}</Button>}
    >
      <div className="tw-space-y-4">
        <p className="tw-flex tw-items-start tw-gap-2 tw-text-sm tw-leading-snug tw-text-slate-500 dark:tw-text-slate-400">
          <LuInfo aria-hidden="true" className="tw-mt-0.5 tw-h-4 tw-w-4 tw-shrink-0" />
          {t('employees.modals.missingDocs.hint')}
        </p>

        <div className="tw-flex tw-flex-col tw-gap-3 sm:tw-flex-row sm:tw-items-center">
          <div className="tw-flex tw-flex-wrap tw-gap-2">
            {countsByType.map(type => (
              <span key={type.key} className={`tw-inline-flex tw-items-center tw-gap-2 tw-rounded-full tw-px-3 tw-py-1.5 tw-text-xs tw-font-semibold tw-text-slate-600 dark:tw-text-slate-300 ${GLASS_CHIP}`}>
                {cvTypeLabel(t, type.key, type.label)}
                <span className="tw-grid tw-h-5 tw-min-w-[1.25rem] tw-place-items-center tw-rounded-full tw-bg-rose-500 tw-px-1.5 tw-text-[11px] tw-font-bold tw-leading-none tw-text-white tw-tabular-nums">
                  {type.count}
                </span>
              </span>
            ))}
          </div>
          <div className="tw-relative sm:tw-ml-auto sm:tw-w-60">
            <LuSearch aria-hidden="true" className="tw-pointer-events-none tw-absolute tw-left-3.5 tw-top-1/2 tw-h-4 tw-w-4 -tw-translate-y-1/2 tw-text-slate-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={t('employees.modals.missingDocs.search')}
              aria-label={t('employees.modals.missingDocs.search')}
              className={`${GLASS_FIELD} tw-pl-10 ${searchTerm ? 'tw-pr-10' : ''}`}
            />
            {searchTerm && (
              <button
                type="button"
                onClick={() => setSearchTerm('')}
                aria-label={t('common.clearSearch')}
                className="tw-absolute tw-right-1.5 tw-top-1/2 tw-grid tw-h-7 tw-w-7 -tw-translate-y-1/2 tw-place-items-center tw-rounded-full tw-bg-transparent tw-text-slate-400 hover:tw-bg-slate-900/[0.06] hover:tw-text-slate-700 dark:hover:tw-bg-white/10 dark:hover:tw-text-white"
              >
                <LuX aria-hidden="true" className="tw-h-4 tw-w-4" />
              </button>
            )}
          </div>
        </div>

        {renderList()}
      </div>
    </Modal>
  );
};

MissingDocumentsModal.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.shape({
    employee: PropTypes.object.isRequired,
    missing: PropTypes.arrayOf(PropTypes.shape({ key: PropTypes.string, label: PropTypes.string })).isRequired,
  })).isRequired,
  loading: PropTypes.bool,
  error: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onOpenFiles: PropTypes.func,
};

export default MissingDocumentsModal;
